import React, { useState } from 'react';
import { Container, Box, Typography, TextField, Button, Paper, IconButton, InputAdornment } from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
// @ts-ignore: imported for side effects, no type declarations for CSS module
import 'react-phone-input-2/lib/material.css';
import { toast } from '../../../components/common/SnackBar/snackBar';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { checkMobileExist, sendOtpForSignup } from '../../../api/AuthAPI';
import registerPageStyles from '../../../styles/PublicPage/register';
import { PATH_NAME } from '../../../configs/pathName';
import { typo } from '../../../assets/fonts/font';
import ForgetPasswordPageStyles from '../../../styles/PublicPage/resetPassword';
import colorConfigs from '../../../configs/colorConfigs';
import CircularProgress from '@mui/material/CircularProgress';

const RegisterPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const UserRegisterData = location.state || {};

    const [phone, setPhone] = useState(UserRegisterData.phone || "");
    const [countryCode, setCountryCode] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [password, setPassword] = useState(UserRegisterData.password || "");
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false); // Add loading state

    const handlePhoneChange = (value: string, data: any) => {
        const phoneNumber = value.slice(data.dialCode.length); 
        setPhone(phoneNumber);
        setCountryCode(`+${data.dialCode}`);
    };

    const handleSendOtp = async () => {
      const phoneNumber = phone.replace(/[^0-9]/g, "");
  
      if (phoneNumber && countryCode) {
          try {
              setIsLoading(true);
              const mobileExistsResponse = await checkMobileExist(`${countryCode}${phoneNumber}`);
              
              if (mobileExistsResponse.result) {
                  const response = await sendOtpForSignup(`${countryCode}${phoneNumber}`);
                  
                  if (response.success) {
                      setOtpSent(true);
                      toast.success("OTP sent successfully!");
                      navigate(PATH_NAME.VERIFY_OTP, {
                          state: { country_code: countryCode, mobile: phoneNumber, password }
                      });
                  } else {
                      toast.error("Failed to send OTP.");
                  }
              } else {
                  toast.error("This account already exists. Please use login details from Shake Salad account.");
              }
          } catch (error:any) {
            toast.error(error.message || "An error occurred. Please try again.");
          } finally {
              setIsLoading(false);
          }
      } else {
          toast.error("Invalid phone number.");
          setIsLoading(false);
      }
    };

    const handleRegister = async () => {

      if (!phone || !password) {
        toast.error("Please enter valid Password.");
        return;
      }

      if (!otpSent && !isLoading) { 
          handleSendOtp();
      }
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    return (
        <>
            <Box sx={registerPageStyles.container}>
                <Container maxWidth="xs" sx={registerPageStyles.contentContainer}>
                    <Paper elevation={0} sx={registerPageStyles.paper}>
                        <Typography variant="h4" component="h1" align="center" gutterBottom sx={{ ...typo.sectionHeadline }}>
                            Create new Account
                        </Typography>
                        <Typography variant="body2" align="center" gutterBottom sx={{ ...typo.bodyContent }}>
                            Already Registered? {" "}
                            <Link to={PATH_NAME.LOGIN} style={{...typo.bodyContent, ...ForgetPasswordPageStyles.loginLink}}>
                                Log in here
                            </Link>
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                            <PhoneInput
                                country={'sg'}
                                value={countryCode + phone}
                                onChange={handlePhoneChange}
                                inputProps={{
                                    name: 'phone',
                                    required: true,
                                    autoFocus: true,
                                }}
                                containerStyle={{
                                    width: "100%",
                                    border: "1px solid",
                                    borderColor:colorConfigs.primary,
                                    borderRadius: "4px",
                                    ...typo.bodyContent,
                                  }}
                                  inputStyle={{
                                    width: "100%",
                                    border: "none",
                                    outline: "none",
                                    boxShadow: "none",
                                    ...typo.bodyContent,
                                  }}                            
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="Password"
                                type={showPassword ? "text" : "password"}
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label="toggle password visibility"
                                          onClick={handleClickShowPassword}
                                          edge="end"
                                        >
                                          {showPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                    sx: {
                                      input: {
                                        ...typo.bodyContent,
                                      },
                                    },
                                    inputProps: {
                                      autoCapitalize: "none",
                                    },
                                  }}
                                  InputLabelProps={{
                                    sx: {
                                      ...typo.bodyContent,
                                    },
                                  }}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      "& fieldset": {
                                        ...typo.bodyContent,
                                      },
                                      "&.Mui-focused fieldset": {
                                        borderColor: colorConfigs.primary,
                                        ...typo.bodyContent,
                                      },
                                    },
                                    "& .MuiInputLabel-root": {},
                                    "& .MuiInputLabel-root.Mui-focused": {
                                      ...typo.bodyContent,
                                    },
                                  }}
                                />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                sx={{...typo.button, ...registerPageStyles.button}}
                                onClick={handleRegister}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    "Sign up"
                                )}
                            </Button>
                        </Box>
                    </Paper>
                </Container>
            </Box>
        </>
    );
};

export default RegisterPage;